import { ReactNode, useLayoutEffect, useRef, useState } from 'react'

import { Button } from '@mui/material'
import {
  getAWSRegionCode,
  getAWSRegionName
} from '@/components/EventSettings/EventSettingsActions/EventSettingsActions.helpers'
import EventSettingsActionsStatus from '@/components/EventSettings/EventSettingsActions/EventSettingsActionsStatus/EventSettingsActionsStatus'
import EventSettingsRow from '@/components/EventSettings/EventSettingsRow'
import ContainerLoader from '@/components/LoaderContainer/ContainerLoader'
import { useGetEvent } from '@/service/eventService'
import { deleteProvision, postProvision, useGetPipelineStatus } from '@/service/streamService'
import { StreamEvent } from '@/types/streams/event'
import getResponseErrorMessage from '@/utils/_legacy/getResponseErrorMessage'
import { notifyError } from '@/utils/notifyError'

const Container = ({ children }: { children: ReactNode }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerHeight, setContainerHeight] = useState<number>()

  useLayoutEffect(() => {
    if (containerRef?.current?.offsetHeight) {
      setContainerHeight(containerRef?.current?.offsetHeight)
    }
  }, [children])

  return (
    <div
      data-testid="event-settings-actions-container"
      style={{
        transition: 'height 0.3s ease',
        height: containerHeight ? `${containerHeight}px` : 'auto',
        overflow: 'hidden'
      }}
    >
      <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
    </div>
  )
}

const EventSettingsActions = ({ event: initialFetchedEvent }: { event: StreamEvent }) => {
  const [togglingProvision, setTogglingProvision] = useState(false)
  const { data: streamEvent } = useGetEvent(initialFetchedEvent.id)
  const { streamingConfig, ...event } = streamEvent || initialFetchedEvent
  const { data: pipelineStatus } = useGetPipelineStatus(initialFetchedEvent.id, streamingConfig?.id)

  const toggleProvision = async () => {
    setTogglingProvision(true)
    try {
      const awsRegionCode = getAWSRegionCode(event)
      if (streamingConfig) {
        if (!awsRegionCode) return
        await deleteProvision(String(event.id), String(awsRegionCode))
      } else {
        await postProvision(String(event.id), String(awsRegionCode))
      }
    } catch (error) {
      notifyError(
        `Error ${streamingConfig ? 'un' : ''}provisioning stream: ` + getResponseErrorMessage(error)
      )
    } finally {
      setTogglingProvision(false)
    }
  }

  return (
    <>
      <Container>
        {event?.estimatedProvisionTime ? (
          <EventSettingsRow
            col1="Estimated Auto Provisioning Time"
            col2={event.estimatedProvisionTime}
          />
        ) : null}
        <EventSettingsRow col1="Region" col2={getAWSRegionName(event)} />
        <EventSettingsRow
          col1="Status"
          col2={
            <EventSettingsActionsStatus
              pipelineStatus={pipelineStatus || null}
              service="pipeline"
            />
          }
        />
        {pipelineStatus?.flows.map((flow, i) => {
          return (
            <EventSettingsRow
              key={`${flow}-${i}`}
              col1="MediaConnect Flow"
              col2={
                <EventSettingsActionsStatus
                  pipelineStatus={pipelineStatus}
                  service="flow"
                  index={i}
                />
              }
            />
          )
        })}
        {pipelineStatus?.channels.map((channel, i) => {
          return (
            <EventSettingsRow
              key={`${channel}-${i}`}
              col1="MediaLive Channel"
              col2={
                <EventSettingsActionsStatus
                  pipelineStatus={pipelineStatus}
                  service="channel"
                  index={i}
                />
              }
            />
          )
        })}
      </Container>
      <div
        style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', padding: '8px 16px' }}
      >
        {/* TODO: TEMPORARY DISABLE (USE togglingProvision) */}
        <Button
          disabled={true}
          variant="contained"
          style={{ width: '150px', height: '29px' }}
          onClick={toggleProvision}
        >
          {togglingProvision ? (
            <ContainerLoader style={{ width: '18px', height: '18px' }} />
          ) : (
            <div>AWS {streamingConfig ? 'Unp' : 'P'}rovision</div>
          )}
        </Button>
      </div>
    </>
  )
}

export default EventSettingsActions
